import twemoji from 'twemoji';

// Cache for parsed emojis to avoid re-parsing
const emojiCache = new Map<string, string>();

const TWEMOJI_SVG_BASE = 'https://cdn.jsdelivr.net/gh/jdecked/twemoji@17.0.3/assets/svg/';
const ZERO_WIDTH_JOINER = String.fromCharCode(0x200d);
const VARIATION_SELECTOR_16 = new RegExp(String.fromCharCode(0xfe0f), 'g');

/**
 * Converts an emoji character (or ZWJ sequence) to a Twemoji <img> tag, with caching.
 * This is especially useful for flag emojis (which don't render properly on Windows)
 * and for brand-new emoji code points that a device's font hasn't been updated to include yet.
 *
 * The markup is built by hand instead of calling twemoji.parse(): the installed `twemoji`
 * npm package bundles a hardcoded regex for *finding* emoji within arbitrary text, frozen
 * at whatever Unicode version was current when that package version was published. Once a
 * newer Unicode release adds code points the regex has never heard of, parse() silently
 * leaves them as plain text instead of converting them. Every caller of this function already
 * knows it's handing us a real, complete emoji, so detection is unnecessary — we go straight
 * from character to codepoint filename using twemoji's own (version-independent) converter,
 * which keeps working for future Unicode releases without needing an npm package upgrade.
 * The jdecked/twemoji fork is used for the image assets themselves since it tracks new
 * Unicode emoji releases faster than the original twitter/twemoji repo; SVG keeps images
 * crisp at any display size.
 */
export const parseEmoji = (emoji: string): string => {
  // Check cache first
  if (emojiCache.has(emoji)) {
    return emojiCache.get(emoji)!;
  }

  // Twemoji filenames drop the variation selector (FE0F) except within ZWJ sequences,
  // where it's part of the joined codepoint chain - mirrors twemoji's own convention.
  const codePointSource = emoji.includes(ZERO_WIDTH_JOINER)
    ? emoji
    : emoji.replace(VARIATION_SELECTOR_16, '');
  const codePoint = twemoji.convert.toCodePoint(codePointSource);
  const src = `${TWEMOJI_SVG_BASE}${codePoint}.svg`;
  const parsed = `<img class="emoji" draggable="false" alt="${emoji}" src="${src}"/>`;

  emojiCache.set(emoji, parsed);
  return parsed;
};

/**
 * Checks if an emoji is a flag emoji (uses Regional Indicator Symbols)
 */
export const isFlagEmoji = (emoji: string): boolean => {
  // Regional Indicator Symbols range: U+1F1E6 to U+1F1FF
  const regionalIndicatorRegex = /[\u{1F1E6}-\u{1F1FF}]/u;
  return regionalIndicatorRegex.test(emoji);
};
